/**
 * The render page (`/render/<token>`): an item's document or spreadsheet drawn for a picture of it, which the server's
 * headless browser takes for an agent (server/src/render.rs, read_file's `format: image`). Its link reads that item
 * alone for a few minutes, as the agent may; the query says what to show: a document's `page` (1 the first), or a
 * spreadsheet's `sheet` and `range`. Once it's drawn (its fonts in, its pictures loaded), what to picture is `[data-shot]`
 * (that page, the grid; or, past a document's end, a line saying how many pages it has) and the page says it's ready
 * (`data-render-ready` on its root), which the browser waits for. With `print=1` it's drawn to be printed, its PDF
 * (the server's export: api/render.rs): a document's pages each a sheet of paper its size, a workbook's sheets as
 * printed (SheetPrint.tsx) on A4. With `thumb=1` it's for a thumbnail: a spreadsheet without headings or a selection. With `pdf=1` it's a PDF's first page instead (a PDF's thumbnail, or a presentation's:
 * thumbs.rs), drawn by the app's PDF engine.
 */

import { useEffect, useRef, useState } from "preact/hooks";
import { api } from "../api/client.ts";
import type { DocumentOut, PdfLinkOut } from "../api/types.gen.ts";
import { locale, t } from "../i18n/index.ts";
import { location } from "../lib/router.ts";
import { type DocumentsModule, documentsChunk } from "../office/documentsChunk.ts";
import { PRINT_MARGIN } from "../office/sheet/printPage.ts";
import { pdfEngine } from "../ui/pdf/load.ts";

/** How long the page waits for what it draws to be there before showing what it has. */
const WAIT_MS = 20_000;
const POLL_MS = 100;

const frame = () => new Promise<void>((done) => requestAnimationFrame(() => done()));

/** Waits until `ready` says what's there, or a while has passed. */
async function until<T>(ready: () => T | null): Promise<T | null> {
  const end = Date.now() + WAIT_MS;
  for (;;) {
    const got = ready();
    if (got !== null || Date.now() > end) {
      return got;
    }
    await new Promise((done) => setTimeout(done, POLL_MS));
  }
}

/** How long a printed page waits for what's drawn later than its pictures (3-D, by WebGL), ms. */
const SETTLE_MS = 400;

/** The printed page's paper: a document's its own size (its margins are on its pages), a workbook's A4 (a wide sheet's
 *  turned: SheetPrint.tsx). */
function paperCss(doc: DocumentOut): string {
  if (doc.kind === "sheet") {
    const m = `${PRINT_MARGIN}px`;
    return `@page { size: A4; margin: ${m}; } @page turned { size: A4 landscape; margin: ${m}; }`;
  }
  const page = (doc.content as { page?: { w?: number; h?: number } }).page;
  return `@page { size: ${page?.w ?? 794}px ${page?.h ?? 1123}px; margin: 0; } html, body { margin: 0; }`;
}

/**
 * A printed document's blocks pushed onto a later page (their room above them reaches past a page's end: a page break,
 * a page filled) marked to start that page when printed (office.css's `[data-print-break]`), only their place in it
 * above them: the browser doesn't cut a block's room above it, it'd move the block whole. (Not their own padding: the
 * layout would put it back.)
 */
function printBreaks(root: HTMLElement, h: number): void {
  const over = root.querySelector<HTMLElement>(".xdoc-over");
  if (!over) {
    return;
  }
  const top = over.getBoundingClientRect().top;
  const pushed: [HTMLElement, number][] = [];
  for (const el of over.querySelectorAll<HTMLElement>("[style*='padding-top']")) {
    const pad = Number.parseFloat(el.style.paddingTop) || 0;
    const from = el.getBoundingClientRect().top - top;
    const at = Math.floor((from + pad + 0.5) / h);
    if (pad > 0 && Math.floor(from / h) < at) {
      pushed.push([el, Math.max(from + pad - at * h, 0)]);
    }
  }
  for (const [el, pad] of pushed) {
    el.dataset["printBreak"] = "";
    el.style.setProperty("--print-pad", `${pad}px`);
  }
}

/** Marks what to picture in `root` once it's drawn, and says the page is ready. */
async function markReady(root: HTMLElement, kind: "doc" | "sheet", page: number, print: boolean): Promise<void> {
  await document.fonts.ready;
  const target = await until(() =>
    print
      ? root.querySelector<HTMLElement>(kind === "sheet" ? ".xsheet-print" : ".xdoc-page")
        ? root
        : null
      : kind === "sheet"
        ? root.querySelector<HTMLElement>("[role=grid]")
        : root.querySelectorAll<HTMLElement>(".xdoc-page").length > 0
          ? root.querySelectorAll<HTMLElement>(".xdoc-page")
          : null,
  );
  // Its pictures in.
  await Promise.all(
    [...root.querySelectorAll("img")].map((img) =>
      img.complete ? null : new Promise((done) => img.addEventListener("loadend", done, { once: true })),
    ),
  );
  await frame();
  await frame();
  if (print) {
    await new Promise((done) => setTimeout(done, SETTLE_MS));
    const paper = root.querySelector<HTMLElement>(".xdoc-page");
    if (kind === "doc" && paper) {
      printBreaks(root, paper.getBoundingClientRect().height);
    }
    root.dataset["shot"] = "";
    root.dataset["renderReady"] = "";
    return;
  }
  if (target instanceof NodeList) {
    const p = target[page - 1];
    if (p) {
      p.dataset["shot"] = "";
    } else {
      const note = document.createElement("p");
      note.dataset["shot"] = "";
      note.className = "p-4 text-sm";
      note.textContent = t("render.noPage", { page, pages: target.length });
      root.prepend(note);
    }
  } else if (target) {
    target.dataset["shot"] = "";
  }
  root.dataset["renderReady"] = "";
}

/** A PDF's first page's width, CSS pixels (its height as the page is). */
const PDF_WIDTH = 794;

/** A PDF's first page (`pdf=1`), drawn by the app's PDF engine for its thumbnail. */
function PdfFirstPage({ token }: { token: string }) {
  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!canvas) {
      return;
    }
    let stop = (): void => undefined;
    const draw = async () => {
      const [{ url }, engine] = await Promise.all([
        api.get<PdfLinkOut>(`/render/${encodeURIComponent(token)}/pdf`),
        pdfEngine.load(),
      ]);
      const opening = engine.open(url);
      stop = opening.cancel;
      const doc = await opening.promise;
      stop = doc.destroy;
      const page = await doc.page(1);
      const zoom = PDF_WIDTH / page.width;
      canvas.width = PDF_WIDTH;
      canvas.height = Math.round(page.height * zoom);
      await page.render(canvas, zoom, 1).promise;
      canvas.dataset["shot"] = "";
      canvas.parentElement?.setAttribute("data-render-ready", "");
    };
    draw().catch(() => setFailed(true));
    return () => stop();
  }, [token, canvas]);
  if (failed) {
    return (
      <p class="p-4 text-sm" data-shot="" data-render-ready="">
        {t("render.failed")}
      </p>
    );
  }
  return (
    <div class="bg-white">
      <canvas ref={setCanvas} />
    </div>
  );
}

export function RenderPage({ token }: { token: string }) {
  const q = location.value.searchParams;
  return q.get("pdf") === "1" ? <PdfFirstPage token={token} /> : <DocumentPage token={token} />;
}

function DocumentPage({ token }: { token: string }) {
  const q = location.value.searchParams;
  const page = Math.max(Number(q.get("page") ?? "1") || 1, 1);
  const print = q.get("print") === "1";
  const [doc, setDoc] = useState<DocumentOut | null>(null);
  const [mod, setMod] = useState<DocumentsModule | null>(null);
  const [failed, setFailed] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    api.get<DocumentOut>(`/render/${encodeURIComponent(token)}`).then(setDoc, () => setFailed(true));
    documentsChunk.load().then(setMod, () => setFailed(true));
  }, [token]);
  // Its paper (an adopted sheet: the app's policy keeps `<style>` elements' text out).
  useEffect(() => {
    if (!(print && doc)) {
      return;
    }
    const paper = new CSSStyleSheet();
    paper.replaceSync(paperCss(doc));
    document.adoptedStyleSheets = [...document.adoptedStyleSheets, paper];
    return () => {
      document.adoptedStyleSheets = document.adoptedStyleSheets.filter((s) => s !== paper);
    };
  }, [print, doc]);
  useEffect(() => {
    const el = root.current;
    if (doc && mod && el) {
      markReady(el, doc.kind === "sheet" ? "sheet" : "doc", page, print).catch(() => {
        el.dataset["renderReady"] = "";
      });
    }
  }, [doc, mod, page, print]);
  if (failed) {
    // Said, so the browser has something to picture: the link's gone or it isn't a document.
    return (
      <p class="p-4 text-sm" data-shot="" data-render-ready="">
        {t("render.failed")}
      </p>
    );
  }
  return (
    <div ref={root} class="bg-white" {...(print ? { "data-print": "" } : {})}>
      {doc && mod ? (
        <mod.DocumentRender
          doc={doc}
          locale={locale.value}
          sheet={q.get("sheet")}
          range={q.get("range")}
          print={print}
          still={q.get("thumb") === "1"}
        />
      ) : null}
    </div>
  );
}
